import { T } from "../theme";
import { useClientScope } from "../contexts/ClientScopeContext";

// Wave 4f: small affordance next to a user name showing whether the user
// belongs to the firm side or is a client in-house user. Rendered only when
// org_type is set — falsy values (null/undefined, system-authored rows,
// external reviewer tokens) produce no markup so the badge never appears on
// "System" or email-only names.
//
// Mapping:
//   firm, internal  → "Firm"
//   client          → "In-house"
//   other / null    → null (no badge)
//
// Visual sizing matches the existing role badge in Sidebar.tsx: small caps,
// muted colors, kept under 2 lines of ascenders so inline placement is stable.

type OrgType = "firm" | "internal" | "client" | string | null | undefined;

const STYLE: Record<string, { label: string; fg: string; bg: string }> = {
  firm: { label: "Firm", fg: "#2B7A3E", bg: "rgba(43,122,62,.1)" },
  internal: { label: "Firm", fg: "#2B7A3E", bg: "rgba(43,122,62,.1)" },
  client: { label: "In-house", fg: "#6B5B95", bg: "rgba(107,91,149,.12)" },
};

export const OrgBadge = ({ org_type, size = "sm" }: { org_type?: OrgType; size?: "sm" | "xs" }) => {
  const { edition } = useClientScope();
  // In in-house deployments everyone is internal; the badge is noise.
  if (edition === "in_house") return null;
  if (!org_type) return null;
  const s = STYLE[org_type];
  if (!s) return null;
  const fontSize = size === "xs" ? 9 : 10;
  const padding = size === "xs" ? "1px 5px" : "2px 7px";
  return (
    <span
      title={`Organization: ${s.label}`}
      style={{
        display: "inline-block",
        fontSize,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        padding,
        borderRadius: 4,
        color: s.fg,
        backgroundColor: s.bg,
        lineHeight: 1.3,
        verticalAlign: "middle",
      }}
    >
      {s.label}
    </span>
  );
};
